import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ImageFileUpload, postData } from "../../../../utility/Utility";

export default function UpdateHeroSlider() {
  const navigate = useNavigate();
  const { state } = useLocation();


  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    description: "",
    image: null,
    isActive: true,
    existingImage: "",
    _id: "",
  });
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");

  useEffect(() => {
    if (state?.slideData) {
      const { title, subtitle, description, isActive, status, image, _id } = state.slideData;

      setFormData({
        title: title || "",
        subtitle: subtitle || "",
        description: description || "",
        isActive: isActive !== undefined ? isActive : (status === "active"),
        image: null,
        existingImage: image || "",
        _id: _id,
      });

      if (image) {
        setImagePreview(image);
      }
    }
  }, [state]);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image" && files && files[0]) {
      const file = files[0];
      setFormData((prev) => ({ ...prev, [name]: file }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const toggleStatus = () => {
    setFormData((prev) => ({
      ...prev,
      isActive: !prev.isActive
    }));
  };

  const uploadImage = async () => {
    if (!formData.image) return formData.existingImage;

    const imageFormData = new FormData();
    imageFormData.append("file", formData.image);

    try {
      const response = await ImageFileUpload("api/admin/upload?is_upload=s3", imageFormData);


      if (response?.success) {
        // Handle the specific response format where image URL is directly in the data
        if (response.data && response.data.image) {
          return response.data.image;
        }
        return response?.url || response.data?.imageUrl || response.data?.filePath;
      } else {
        toast.error(response?.message || "Image upload failed");
        return null;
      }
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error(error.response?.data?.message || "Error uploading image");
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.subtitle.trim() || !formData.description.trim()) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);

    try {
      // Process image if needed
      let imageUrl = formData.existingImage;
      if (formData.image) {
        imageUrl = await uploadImage();
        if (!imageUrl) {
          toast.error("Image upload failed. Please try again.");
          setLoading(false);
          return;
        }
      }

      // Prepare payload for API
      const payload = {
        title: formData.title,
        subtitle: formData.subtitle,
        description: formData.description,
        isActive: formData.isActive,
        image: imageUrl,
      };



      // Call API to update the slide
      const response = await postData(`api/admin/hero-slides/update/${formData._id}`, payload);


      // Handle response
      if (response && (response.success || (response.data && response.data._id) || response._id)) {
        toast.success(response.message || "Hero slide updated successfully!");
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else {
        throw new Error(response?.message || "Failed to update hero slide");
      }
    } catch (error) {
      console.error("Update hero slide error:", error);
      toast.error(error.response?.data?.message || error.message || "Error updating hero slide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <div className="page-title-box">
                <h4 className="mb-1">Hero Slider Management</h4>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">Hero Slider</li>
                  <li className="breadcrumb-item active">Update Slide</li>
                </ol>
              </div>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                <i className="fas fa-arrow-left me-1"></i> Back
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Update Hero Slide</h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Title */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="title" className="form-label fw-medium">
                          Title <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="title"
                          name="title"
                          className="form-control"
                          placeholder="Enter slide title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          minLength={3}
                          maxLength={100}
                        />
                      </div>
                    </div>

                    {/* Subtitle */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="subtitle" className="form-label fw-medium">
                          Subtitle <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="subtitle"
                          name="subtitle"
                          className="form-control"
                          placeholder="Enter slide subtitle"
                          value={formData.subtitle}
                          onChange={handleInputChange}
                          required
                          minLength={3}
                          maxLength={100}
                        />
                      </div>
                    </div>

                    {/* Image */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="image" className="form-label fw-medium">
                          Slide Image
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="file"
                          id="image"
                          name="image"
                          className="form-control"
                          onChange={handleInputChange}
                          accept="image/*"
                        />
                        <small className="text-muted d-block mt-1">
                          Recommended: 1920x1080px, Max 2MB (JPEG/PNG)
                        </small>
                        {imagePreview && (
                          <div className="mt-2">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="img-thumbnail"
                              style={{ maxWidth: "200px", maxHeight: "150px" }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">Status</label>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check form-switch d-flex align-items-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="status"
                            checked={formData.isActive}
                            onChange={toggleStatus}
                            role="switch"
                            style={{ width: "2.5em", height: "1.25em" }}
                          />
                          <label className="form-check-label ms-2" htmlFor="status">
                            {formData.isActive ? (
                              <span className="text-success">Active</span>
                            ) : (
                              <span className="text-muted">Inactive</span>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label htmlFor="description" className="form-label fw-medium">
                          Description <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <textarea
                          className="form-control"
                          id="description"
                          name="description"
                          rows={4}
                          placeholder="Describe the slide..."
                          value={formData.description}
                          onChange={handleInputChange}
                          required
                          minLength={10}
                        />
                        <small className="text-muted">Minimum 10 characters</small>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row mt-4">
                      <div className="col-md-6 offset-md-3">
                        <button
                          type="submit"
                          className="btn btn-primary py-2"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Processing...
                            </>
                          ) : (
                            "Update Slide"
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}